// Settings panel with SSH key management

import { listSSHKeys, addSSHKey, deleteSSHKey, logout } from './auth';
import { listProfiles, createProfile, deleteProfile, forkProfile, renameProfile, type Profile } from './profiles';

export interface SettingsOptions {
  container: HTMLElement;
  token: string;
  userEmail: string;
  onClose: () => void;
  onLogout: () => void;
  // Called after any successful create, fork or delete so main.ts can redraw
  // the profile switcher bar. main.ts owns the bar's state; this panel never
  // reaches into it directly.
  onProfilesChanged: () => void;
}

export async function renderSettingsPanel(options: SettingsOptions): Promise<void> {
  const { container, token, userEmail, onClose, onLogout, onProfilesChanged } = options;

  // Create settings panel
  const panel = document.createElement('div');
  panel.className = 'settings-panel';

  panel.innerHTML = `
    <div class="settings-header">
      <h2>Settings</h2>
      <button class="settings-close" aria-label="Close">&times;</button>
    </div>
    <div class="settings-content">
      <section class="settings-section">
        <h3>Profiles</h3>
        <p class="settings-description">
          Each profile is a separate character with its own settings, plugins and
          saved data. Profiles inherit from a base that is maintained for you;
          fork one to take full ownership of it.
        </p>

        <div class="profiles-list" id="profiles-list">
          <div class="loading-spinner">Loading...</div>
        </div>

        <div class="profile-add">
          <h4>New Profile</h4>
          <div class="profile-add-form">
            <input type="text" id="profile-new-name" placeholder="Name (a-z, 0-9, - and _)" maxlength="32">
            <input type="text" id="profile-new-character" placeholder="Character name (optional)" maxlength="32">
            <select id="profile-new-base">
              <option value="player">Player - the full 3scapes setup</option>
              <option value="wizard">Wizard - the player setup with a file browser instead of a guild pane</option>
              <option value="bare">Bare - commands and a connection, nothing else</option>
            </select>
            <label class="profile-fork-toggle">
              <input type="checkbox" id="profile-new-fork">
              Copy the base into my profile so I can edit it (stops automatic updates)
            </label>
            <button id="profile-create-btn" class="btn-primary">Create Profile</button>
            <div id="profile-create-error" class="error-message"></div>
          </div>
        </div>
      </section>
      <section class="settings-section">
        <h3>SSH Keys</h3>
        <p class="settings-description">
          Add SSH keys to access your session via terminal. Connect with:
          <code>ssh lera@lera.stalverk.nu</code>
        </p>

        <div class="ssh-keys-list" id="ssh-keys-list">
          <div class="loading-spinner">Loading...</div>
        </div>

        <div class="ssh-add-key">
          <h4>Add New Key</h4>
          <div class="ssh-add-form">
            <input type="text" id="ssh-key-name" placeholder="Key name (e.g., MacBook)" maxlength="64">
            <textarea id="ssh-key-public" placeholder="Paste your public key (ssh-ed25519 AAAA... or ssh-rsa AAAA...)" rows="3"></textarea>
            <button id="ssh-add-btn" class="btn-primary">Add Key</button>
            <div id="ssh-add-error" class="error-message"></div>
          </div>
          <div class="ssh-instructions">
            <details>
              <summary>How to generate an SSH key</summary>
              <ol>
                <li>Open a terminal</li>
                <li>Run: <code>ssh-keygen -t ed25519 -C "${userEmail}"</code></li>
                <li>Press Enter to accept the default location</li>
                <li>Copy your public key: <code>cat ~/.ssh/id_ed25519.pub</code></li>
                <li>Paste it above</li>
              </ol>
            </details>
          </div>
        </div>
      </section>
      <section class="settings-section settings-logout-section">
        <button id="settings-logout-btn" class="btn-logout">Log Out</button>
      </section>
    </div>
  `;

  container.appendChild(panel);

  // Close button handler
  panel.querySelector('.settings-close')?.addEventListener('click', () => {
    panel.remove();
    onClose();
  });

  // Close on escape key
  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      panel.remove();
      onClose();
      document.removeEventListener('keydown', escHandler);
    }
  };
  document.addEventListener('keydown', escHandler);

  // Logout button handler
  panel.querySelector('#settings-logout-btn')?.addEventListener('click', async () => {
    await logout(token);
    panel.remove();
    onLogout();
  });

  // Load profiles and SSH keys (independent lists, load in parallel)
  const profilesList = panel.querySelector('#profiles-list') as HTMLElement;
  const keysList = panel.querySelector('#ssh-keys-list') as HTMLElement;
  await Promise.all([
    loadProfiles(profilesList, token, onProfilesChanged),
    loadSSHKeys(keysList, token),
  ]);

  // Create profile handler
  const createNameInput = panel.querySelector('#profile-new-name') as HTMLInputElement;
  const createCharacterInput = panel.querySelector('#profile-new-character') as HTMLInputElement;
  const createBaseSelect = panel.querySelector('#profile-new-base') as HTMLSelectElement;
  const createForkCheckbox = panel.querySelector('#profile-new-fork') as HTMLInputElement;
  const createBtn = panel.querySelector('#profile-create-btn') as HTMLButtonElement;
  const createError = panel.querySelector('#profile-create-error') as HTMLElement;

  createBtn.addEventListener('click', async () => {
    createError.textContent = '';
    createBtn.disabled = true;
    createBtn.textContent = 'Creating...';
    try {
      await createProfile(token, {
        name: createNameInput.value.trim(),
        character: createCharacterInput.value.trim() || undefined,
        base: createBaseSelect.value as 'wizard' | 'player' | 'bare',
        mode: createForkCheckbox.checked ? 'fork' : 'inherit',
      });
      createNameInput.value = '';
      createCharacterInput.value = '';
      createForkCheckbox.checked = false;
      await loadProfiles(profilesList, token, onProfilesChanged);
      onProfilesChanged();
    } catch (err) {
      createError.textContent = err instanceof Error ? err.message : 'Failed to create the profile';
    } finally {
      createBtn.disabled = false;
      createBtn.textContent = 'Create Profile';
    }
  });

  // Add key handler
  const addBtn = panel.querySelector('#ssh-add-btn') as HTMLButtonElement;
  const nameInput = panel.querySelector('#ssh-key-name') as HTMLInputElement;
  const keyInput = panel.querySelector('#ssh-key-public') as HTMLTextAreaElement;
  const errorDiv = panel.querySelector('#ssh-add-error') as HTMLElement;

  addBtn.addEventListener('click', async () => {
    const name = nameInput.value.trim();
    const publicKey = keyInput.value.trim();

    // Validate
    if (!name) {
      errorDiv.textContent = 'Please enter a name for this key';
      return;
    }
    if (!publicKey) {
      errorDiv.textContent = 'Please paste your public key';
      return;
    }
    if (!publicKey.startsWith('ssh-')) {
      errorDiv.textContent = 'Invalid key format. Key should start with ssh-ed25519, ssh-rsa, etc.';
      return;
    }

    // Disable button during request
    addBtn.disabled = true;
    addBtn.textContent = 'Adding...';
    errorDiv.textContent = '';

    try {
      await addSSHKey(token, name, publicKey);
      // Clear form
      nameInput.value = '';
      keyInput.value = '';
      // Reload keys list
      await loadSSHKeys(keysList, token);
    } catch (err) {
      errorDiv.textContent = err instanceof Error ? err.message : 'Failed to add key';
    } finally {
      addBtn.disabled = false;
      addBtn.textContent = 'Add Key';
    }
  });
}

async function loadProfiles(container: HTMLElement, token: string, onProfilesChanged: () => void): Promise<void> {
  container.innerHTML = '<div class="loading-spinner">Loading...</div>';

  let profiles: Profile[];
  try {
    profiles = await listProfiles(token);
  } catch (err) {
    container.innerHTML = '';
    const error = document.createElement('p');
    error.className = 'error-message';
    error.textContent = err instanceof Error ? err.message : 'Failed to load profiles';
    container.appendChild(error);
    return;
  }

  container.innerHTML = '';

  if (profiles.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'no-keys';
    empty.textContent = 'No profiles yet';
    container.appendChild(empty);
    return;
  }

  for (const p of profiles) {
    container.appendChild(renderProfileRow(p, token, container, onProfilesChanged));
  }
}

// display_name is unvalidated user input (no server-side length limit or
// character rules -- see profile-bar.ts). Every field built here goes through
// createElement/textContent rather than innerHTML, and nothing profile-derived
// is placed into an HTML attribute, so there is no attribute-escaping gap to
// close (contrast profile-bar.ts's escapeAttr, needed there because tab
// tooltips are built as attribute values).
function renderProfileRow(p: Profile, token: string, listContainer: HTMLElement, onProfilesChanged: () => void): HTMLElement {
  const row = document.createElement('div');
  row.className = 'profile-manage-item';
  row.dataset.manageProfile = p.name;

  const info = document.createElement('div');
  info.className = 'profile-manage-info';

  const nameLine = document.createElement('div');
  nameLine.className = 'profile-manage-name';
  nameLine.textContent = p.display_name;
  info.appendChild(nameLine);

  const meta = document.createElement('div');
  meta.className = 'profile-manage-meta';
  meta.textContent = `${p.base} · ${p.forked ? 'Forked' : `Inherits from ${p.base}`}`;
  info.appendChild(meta);

  if (p.forked && p.behind_by > 0) {
    const stale = document.createElement('div');
    stale.className = 'profile-manage-stale';
    stale.textContent = `${p.behind_by} version${p.behind_by === 1 ? '' : 's'} behind the ${p.base} base`;
    info.appendChild(stale);
  }

  row.appendChild(info);

  const actions = document.createElement('div');
  actions.className = 'profile-manage-actions';

  const renameBtn = document.createElement('button');
  renameBtn.className = 'profile-rename btn-secondary';
  renameBtn.type = 'button';
  renameBtn.textContent = 'Rename';
  renameBtn.addEventListener('click', async () => {
    // Only display_name changes here -- the slug (p.name) is what every
    // other API call and data-manage-profile identify the row by, and the
    // server's PATCH only ever touches display_name.
    const next = prompt('New display name:', p.display_name);
    if (next === null) return; // dismissed
    const trimmed = next.trim();
    if (!trimmed || trimmed === p.display_name) return; // no-op
    renameBtn.disabled = true;
    try {
      await renameProfile(token, p.name, trimmed);
      onProfilesChanged();
      await loadProfiles(listContainer, token, onProfilesChanged);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to rename the profile');
      renameBtn.disabled = false;
    }
  });
  actions.appendChild(renameBtn);

  if (!p.forked) {
    const forkBtn = document.createElement('button');
    forkBtn.className = 'profile-fork btn-secondary';
    forkBtn.type = 'button';
    forkBtn.textContent = 'Fork';
    forkBtn.addEventListener('click', async () => {
      forkBtn.disabled = true;
      try {
        await forkProfile(token, p.name);
        onProfilesChanged();
        await loadProfiles(listContainer, token, onProfilesChanged);
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to fork the profile');
        forkBtn.disabled = false;
      }
    });
    actions.appendChild(forkBtn);
  }

  const deleteBtn = document.createElement('button');
  deleteBtn.className = 'profile-delete btn-danger';
  deleteBtn.type = 'button';
  deleteBtn.textContent = 'Delete';
  deleteBtn.addEventListener('click', async () => {
    if (!confirm(`Delete the profile "${p.display_name}"? Its settings and saved data are removed permanently.`)) return;
    deleteBtn.disabled = true;
    try {
      await deleteProfile(token, p.name);
      onProfilesChanged();
      await loadProfiles(listContainer, token, onProfilesChanged);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete the profile');
      deleteBtn.disabled = false;
    }
  });
  actions.appendChild(deleteBtn);

  row.appendChild(actions);
  return row;
}

async function loadSSHKeys(container: HTMLElement, token: string): Promise<void> {
  container.innerHTML = '<div class="loading-spinner">Loading...</div>';

  try {
    const keys = await listSSHKeys(token);

    if (keys.length === 0) {
      container.innerHTML = '<p class="no-keys">No SSH keys configured</p>';
      return;
    }

    container.innerHTML = keys.map(key => `
      <div class="ssh-key-item" data-fingerprint="${escapeHtml(key.fingerprint)}">
        <div class="ssh-key-info">
          <span class="ssh-key-name">${escapeHtml(key.name)}</span>
          <span class="ssh-key-fingerprint">${escapeHtml(key.fingerprint)}</span>
          <span class="ssh-key-date">Added ${formatDate(key.created_at)}</span>
          ${key.last_used_at ? `<span class="ssh-key-used">Last used ${formatDate(key.last_used_at)}</span>` : ''}
        </div>
        <button class="ssh-key-delete" title="Delete key">&times;</button>
      </div>
    `).join('');

    // Add delete handlers
    container.querySelectorAll('.ssh-key-delete').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const item = (e.target as HTMLElement).closest('.ssh-key-item') as HTMLElement;
        const fingerprint = item.dataset.fingerprint;
        if (!fingerprint) return;

        if (!confirm('Delete this SSH key? You will no longer be able to connect with it.')) {
          return;
        }

        try {
          await deleteSSHKey(token, fingerprint);
          item.remove();
          // Check if list is now empty
          if (container.querySelectorAll('.ssh-key-item').length === 0) {
            container.innerHTML = '<p class="no-keys">No SSH keys configured</p>';
          }
        } catch (err) {
          alert(err instanceof Error ? err.message : 'Failed to delete key');
        }
      });
    });
  } catch (err) {
    container.innerHTML = `<p class="error-message">${err instanceof Error ? err.message : 'Failed to load keys'}</p>`;
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
}

// CSS for settings panel
const settingsStyles = `
.settings-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 500px;
  height: 100vh;
  background: #1e1e2e;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #e0e0e0;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #333;
}

.settings-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #00d4aa;
}

.settings-close {
  background: none;
  border: none;
  color: #888;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
}

.settings-close:hover {
  color: #fff;
}

.settings-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.settings-section {
  margin-bottom: 24px;
}

.settings-section h3 {
  margin: 0 0 8px;
  font-size: 1rem;
  color: #fff;
}

.settings-description {
  color: #888;
  font-size: 0.875rem;
  margin: 0 0 16px;
}

.settings-description code {
  background: #2a2a3e;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: #00d4aa;
}

.profiles-list {
  margin-bottom: 20px;
}

.profile-manage-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px;
  background: #2a2a3e;
  border-radius: 8px;
  margin-bottom: 8px;
  gap: 12px;
}

.profile-manage-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.profile-manage-name {
  font-weight: 500;
  color: #fff;
}

.profile-manage-meta {
  font-size: 0.75rem;
  color: #888;
  text-transform: capitalize;
}

.profile-manage-stale {
  font-size: 0.75rem;
  color: #cd9b00;
}

.profile-manage-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.btn-secondary {
  padding: 6px 12px;
  background: none;
  color: #00d4aa;
  border: 1px solid #00d4aa;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.btn-secondary:hover {
  background: #00d4aa;
  color: #1a1a2e;
}

.btn-secondary:disabled {
  border-color: #444;
  color: #888;
  cursor: not-allowed;
}

.btn-danger {
  padding: 6px 12px;
  background: none;
  color: #ff6666;
  border: 1px solid #ff6666;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.btn-danger:hover {
  background: #ff6666;
  color: #1a1a2e;
}

.btn-danger:disabled {
  border-color: #444;
  color: #888;
  cursor: not-allowed;
}

.profile-add h4 {
  margin: 0 0 12px;
  font-size: 0.875rem;
  color: #888;
}

.profile-add-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.profile-add-form input,
.profile-add-form select {
  padding: 10px 12px;
  background: #2a2a3e;
  border: 1px solid #444;
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 0.875rem;
  font-family: inherit;
}

.profile-add-form input:focus,
.profile-add-form select:focus {
  outline: none;
  border-color: #00d4aa;
}

.profile-fork-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: #888;
  cursor: pointer;
}

.ssh-keys-list {
  margin-bottom: 20px;
}

.ssh-key-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px;
  background: #2a2a3e;
  border-radius: 8px;
  margin-bottom: 8px;
}

.ssh-key-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.ssh-key-name {
  font-weight: 500;
  color: #fff;
}

.ssh-key-fingerprint {
  font-family: "JetBrains Mono", "Fira Code", monospace;
  font-size: 0.75rem;
  color: #888;
  word-break: break-all;
}

.ssh-key-date,
.ssh-key-used {
  font-size: 0.75rem;
  color: #666;
}

.ssh-key-delete {
  background: none;
  border: none;
  color: #666;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 4px 8px;
  margin-left: 8px;
  line-height: 1;
}

.ssh-key-delete:hover {
  color: #ff4444;
}

.no-keys {
  color: #666;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

.ssh-add-key h4 {
  margin: 0 0 12px;
  font-size: 0.875rem;
  color: #888;
}

.ssh-add-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ssh-add-form input,
.ssh-add-form textarea {
  padding: 10px 12px;
  background: #2a2a3e;
  border: 1px solid #444;
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 0.875rem;
  font-family: inherit;
}

.ssh-add-form textarea {
  font-family: "JetBrains Mono", "Fira Code", monospace;
  font-size: 0.75rem;
  resize: vertical;
}

.ssh-add-form input:focus,
.ssh-add-form textarea:focus {
  outline: none;
  border-color: #00d4aa;
}

.btn-primary {
  padding: 10px 16px;
  background: #00d4aa;
  color: #1a1a2e;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-primary:hover {
  background: #00e4ba;
}

.btn-primary:disabled {
  background: #444;
  color: #888;
  cursor: not-allowed;
}

.error-message {
  color: #ff6666;
  font-size: 0.875rem;
}

.loading-spinner {
  color: #888;
  text-align: center;
  padding: 20px;
}

.ssh-instructions {
  margin-top: 16px;
}

.ssh-instructions details {
  font-size: 0.875rem;
  color: #888;
}

.ssh-instructions summary {
  cursor: pointer;
  color: #00d4aa;
}

.ssh-instructions summary:hover {
  text-decoration: underline;
}

.ssh-instructions ol {
  margin: 12px 0 0 0;
  padding-left: 20px;
}

.ssh-instructions li {
  margin-bottom: 8px;
}

.ssh-instructions code {
  background: #2a2a3e;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: #e0e0e0;
  font-size: 0.8em;
}

.settings-logout-section {
  border-top: 1px solid #333;
  padding-top: 24px;
}

.btn-logout {
  width: 100%;
  padding: 10px 16px;
  background: none;
  color: #ff6666;
  border: 1px solid #ff6666;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.btn-logout:hover {
  background: #ff6666;
  color: #1a1a2e;
}
`;

// Inject styles once
let settingsStylesInjected = false;
export function injectSettingsStyles(): void {
  if (settingsStylesInjected) return;
  const style = document.createElement('style');
  style.textContent = settingsStyles;
  document.head.appendChild(style);
  settingsStylesInjected = true;
}
